import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import EmailInput, { EmailInputHandle } from './EmailInput'
import InternalDomainVerificationPanel from './InternalDomainVerificationPanel'

import styles from './BulkMemberActions.module.scss'

import { isApiError } from '@/api'
import {
  AddUsersToEnterpriseResponse,
  EnterpriseRole,
  RemoveUsersFromEnterpriseResponse,
  UpdateUsersRoleResponse,
} from '@/api/generated'
import {
  bulkAddUsersToEnterprise,
  BulkInput,
  removeUsersFromEnterprise,
  uploadRemoveUsersFromEnterprise,
  uploadUpdateRoles,
  uploadUsersToEnterprise,
} from '@/api/internal'
import Button from '@/components/Button'
import Select from '@/components/Select'
import { Switch } from '@/components/Switch'
import Tabs from '@/components/Tabs'
import { useToast } from '@/components/Toast/ToastContext'
import { ASSIGNABLE_ROLE_OPTIONS } from '@/constants/enterprise'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import EnterpriseSuperadmins from '@/pages/app/Enterprises/components/EnterpriseSuperadmins'
import { selectIsInternalSuperAdmin } from '@/store/features/user/userSlice'

const useAddMembers = (enterpriseId: string, inviteMissingUsers: boolean) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: BulkInput) => {
      if (input.type === 'file') {
        return uploadUsersToEnterprise(enterpriseId, input.file, inviteMissingUsers)
      }

      return bulkAddUsersToEnterprise(enterpriseId, input.emails, EnterpriseRole.Member, inviteMissingUsers)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })
}

const useRemoveMembers = (enterpriseId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: BulkInput) => {
      if (input.type === 'file') {
        return uploadRemoveUsersFromEnterprise(enterpriseId, input.file)
      }
      return removeUsersFromEnterprise(enterpriseId, input.emails)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })
}

const useBulkUpdateRoles = (enterpriseId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { file: File; role: EnterpriseRole }) => uploadUpdateRoles(enterpriseId, data.file, data.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })
}

const ACTION_TABS = ['Add Users', 'Remove Users', 'Update Roles', 'Domain Capture']

const DOMAIN_CAPTURE_TAB_INDEX = ACTION_TABS.indexOf('Domain Capture')
const SUPERADMINS_TAB_INDEX = ACTION_TABS.length

interface BulkMemberActionsProps {
  enterpriseId: string
  enterpriseName: string
  onError: (error: string | null) => void
}

type BulkResult = AddUsersToEnterpriseResponse | RemoveUsersFromEnterpriseResponse | UpdateUsersRoleResponse

const formatResultMessage = (
  result: BulkResult,
  action: 'added' | 'removed' | 'updated',
  role?: EnterpriseRole
): { message: string; hasSuccess: boolean; hasError: boolean } => {
  const parts: string[] = []

  const count =
    'added_count' in result
      ? result.added_count
      : 'removed_count' in result
        ? result.removed_count
        : 'updated_count' in result
          ? result.updated_count
          : 0

  if (count > 0) {
    const roleText = action === 'updated' && role ? ` to ${role}` : ''
    parts.push(`${action.charAt(0).toUpperCase() + action.slice(1)} ${count} user(s)${roleText}`)
  }

  const invitedCount = ('added_count' in result ? result.invited_count : 0) ?? 0
  if (invitedCount > 0) {
    parts.push(`${invitedCount} invitation(s) sent`)
  }

  // skipped_count exists on AddUsersToEnterpriseResponse and UpdateUsersRoleResponse
  const skippedCount = 'skipped_count' in result ? result.skipped_count : undefined
  if (skippedCount && skippedCount > 0) {
    const skipReason =
      action === 'added' ? 'already in enterprise' : action === 'updated' ? 'already had this role' : 'not found'
    parts.push(`${skippedCount} ${skipReason}`)
  }

  // failed exists on AddUsersToEnterpriseResponse and UpdateUsersRoleResponse
  const failed = 'failed' in result ? result.failed : undefined
  if (failed?.length) {
    const failedPreview = failed
      .slice(0, 3)
      .map((f) => `${f.email} (${f.reason})`)
      .join(', ')
    const suffix = failed.length > 3 ? ` and ${failed.length - 3} more` : ''
    parts.push(`Failed: ${failedPreview}${suffix}`)
  }

  return {
    message: parts.join('. ') || `No users were ${action}`,
    hasSuccess: count > 0 || invitedCount > 0,
    hasError: (failed?.length ?? 0) > 0,
  }
}

const INPUT_HINT =
  'Enter emails or upload a file with emails separated by commas or newlines (max 100). If a file is chosen, it will be used instead of any text input.'

const inputHint = (activeTab: number, inviteMissingUsers: boolean): string => {
  if (activeTab !== 0) return INPUT_HINT
  if (!inviteMissingUsers) return `${INPUT_HINT} Emails must belong to existing users.`
  return 'Enter or upload up to 100 email addresses, separated by commas or newlines. Existing users are added immediately. New users receive an invitation and create their account when they sign up. If the team has no billing, invited users join as admins.'
}

const BulkMemberActions = ({ enterpriseId, enterpriseName, onError }: BulkMemberActionsProps) => {
  const { updateToast } = useToast()
  const emailInputRef = useRef<EmailInputHandle>(null)
  const isInternalSuperAdmin = useSelector(selectIsInternalSuperAdmin)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const canManageSuperadmins = isInternalSuperAdmin && !impersonating
  const tabs = canManageSuperadmins ? [...ACTION_TABS, 'Superadmins'] : ACTION_TABS

  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    if (!canManageSuperadmins) setActiveTab((tab) => (tab === SUPERADMINS_TAB_INDEX ? 0 : tab))
  }, [canManageSuperadmins])
  const [targetRole, setTargetRole] = useState<EnterpriseRole>(EnterpriseRole.Member)
  const [inviteMissingUsers, setInviteMissingUsers] = useState(false)

  const addMembers = useAddMembers(enterpriseId, inviteMissingUsers)
  const removeMembers = useRemoveMembers(enterpriseId)
  const bulkUpdateRoles = useBulkUpdateRoles(enterpriseId)

  const anyLoading = addMembers.isPending || removeMembers.isPending || bulkUpdateRoles.isPending

  const handleTabChange = (tab: number) => {
    setActiveTab(tab)
    setInviteMissingUsers(false)
    emailInputRef.current?.reset()
    onError(null)
  }

  const handleSuccess = (result: BulkResult, action: 'added' | 'removed' | 'updated') => {
    const { message, hasSuccess, hasError } = formatResultMessage(
      result,
      action,
      action === 'updated' ? targetRole : undefined
    )

    const skippedCount = 'skipped_count' in result ? result.skipped_count : undefined

    if (hasSuccess && !hasError) {
      updateToast(message, 'success')
      emailInputRef.current?.reset()
    } else if (hasSuccess && hasError) {
      onError(message)
      if (action === 'added' && 'failed' in result) {
        emailInputRef.current?.setEmails((result.failed ?? []).map((failure) => failure.email))
      } else {
        emailInputRef.current?.reset()
      }
    } else if (!hasSuccess && !hasError && skippedCount && skippedCount > 0) {
      const skipMessage =
        action === 'added'
          ? 'All users are already in this enterprise'
          : action === 'updated'
            ? 'All users already have this role'
            : 'No users were found to remove'
      updateToast(skipMessage, 'success')
      emailInputRef.current?.reset()
    } else {
      onError(message)
    }
  }

  const handleError = (err: unknown, action: string) => {
    if (isApiError(err)) {
      let detail: string | null = null
      const rawDetail = err.detail as unknown
      if (typeof rawDetail === 'string') {
        detail = rawDetail
      } else if (Array.isArray(rawDetail)) {
        detail = rawDetail.map((d: { msg?: string }) => d.msg ?? JSON.stringify(d)).join('; ')
      }
      onError(detail || err.message || `Failed to ${action}`)
    } else {
      onError(`Failed to ${action}. Please try again.`)
    }
  }

  const formatInvalidEmailsError = (invalidEmails: string[]): string => {
    const preview = invalidEmails.slice(0, 5).join(', ')
    const suffix = invalidEmails.length > 5 ? ` and ${invalidEmails.length - 5} more` : ''
    return `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${preview}${suffix}. Please fix or remove them.`
  }

  const collectInputErrors = (result: { invalidEmails: string[] }): string | null => {
    if (result.invalidEmails.length > 0) return formatInvalidEmailsError(result.invalidEmails)
    return null
  }

  const handleAddUsers = () => {
    const result = emailInputRef.current?.getValidatedInput()
    if (!result) {
      onError('Please enter emails or select a file')
      return
    }
    const inputError = collectInputErrors(result)
    if (inputError) {
      onError(inputError)
      return
    }
    if (!result.input) {
      onError('Please enter emails or select a file')
      return
    }
    onError(null)
    addMembers.mutate(result.input, {
      onSuccess: (response) => handleSuccess(response.data, 'added'),
      onError: (err) => handleError(err, 'add users'),
    })
  }

  const handleRemoveUsers = () => {
    const result = emailInputRef.current?.getValidatedInput()
    if (!result) {
      onError('Please enter emails or select a file')
      return
    }
    const inputError = collectInputErrors(result)
    if (inputError) {
      onError(inputError)
      return
    }
    if (!result.input) {
      onError('Please enter emails or select a file')
      return
    }
    onError(null)
    removeMembers.mutate(result.input, {
      onSuccess: (response) => handleSuccess(response.data, 'removed'),
      onError: (err) => handleError(err, 'remove users'),
    })
  }

  const handleUpdateRoles = () => {
    const input = emailInputRef.current?.getInput()
    if (!input || input.type !== 'file') {
      onError('Please select a file')
      return
    }
    onError(null)
    bulkUpdateRoles.mutate(
      { file: input.file, role: targetRole },
      {
        onSuccess: (response) => handleSuccess(response.data, 'updated'),
        onError: (err) => handleError(err, 'update roles'),
      }
    )
  }

  return (
    <div className={styles.addSection}>
      {/* A result landing after a tab switch would refill the shared input on the wrong tab. */}
      <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={handleTabChange} disabled={anyLoading} />

      {activeTab === DOMAIN_CAPTURE_TAB_INDEX ? (
        <InternalDomainVerificationPanel enterpriseId={enterpriseId} onError={onError} />
      ) : activeTab === SUPERADMINS_TAB_INDEX ? (
        <EnterpriseSuperadmins enterpriseId={enterpriseId} enterpriseName={enterpriseName} />
      ) : (
        <>
          <p className={styles.addHint}>{inputHint(activeTab, inviteMissingUsers)}</p>

          {activeTab === 0 && (
            <>
              <p className={styles.addHint}>Adding existing users will follow the following rules:</p>
              <ul className={styles.addHint}>
                <li>If team has billing: add user and cancel that user&apos;s subscription</li>
                <li>If team has no billing + user has subscription: fail (will not add)</li>
                <li>If team has no billing + user has no subscription: add if eligible for team trial</li>
              </ul>
              <div className={styles.toggleRow}>
                <Switch
                  checked={inviteMissingUsers}
                  onInputChange={setInviteMissingUsers}
                  disabled={anyLoading}
                  aria-label="Invite people who don't have a Flow account"
                />
                <span className={styles.addHint}>Invite people who don&apos;t have a Flow account</span>
              </div>
            </>
          )}

          {activeTab === 2 && (
            <div className={styles.roleSelector}>
              <label>Set role to:</label>
              <Select
                value={targetRole}
                options={ASSIGNABLE_ROLE_OPTIONS}
                onChange={(val) => setTargetRole(val as EnterpriseRole)}
              />
            </div>
          )}

          <EmailInput ref={emailInputRef} isDisabled={anyLoading} fileOnly={activeTab === 2} />

          <div className={styles.buttonGroup}>
            {activeTab === 0 && (
              <Button
                title="Add Users"
                onClick={handleAddUsers}
                type="dark"
                isLoading={addMembers.isPending}
                isDisabled={anyLoading}
              />
            )}
            {activeTab === 1 && (
              <Button
                title="Remove Users"
                onClick={handleRemoveUsers}
                type="dark"
                isLoading={removeMembers.isPending}
                isDisabled={anyLoading}
              />
            )}
            {activeTab === 2 && (
              <Button
                title={`Set to ${targetRole === 'admin' ? 'Admin' : 'Member'}`}
                onClick={handleUpdateRoles}
                type="dark"
                isLoading={bulkUpdateRoles.isPending}
                isDisabled={anyLoading}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default BulkMemberActions
